import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "../icons/FigmaControlIcons";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { DeleteOutlineRounded as DeleteOutlineRoundedIcon } from "../icons/FigmaControlIcons";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";

const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const BORDER = "#E5E7EB";
const LIGHT_BLUE = "#D7E6FF";
const MUTED = "#98A2B3";
const TEXT = "#344054";

function initials(label) {
  const value = String(label || "").trim();
  if (!value) return "?";
  const parts = value.split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join("");
}

function ThreadPreview({ thread }) {
  if (thread.preview_type === "attachment") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: PURPLE }}>
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 15 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: PURPLE,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {thread.preview_attachment_name || "Attachment"}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: MUTED, lineHeight: 1.25 }}>
            {thread.preview_attachment_size || ""}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Typography
      sx={{
        fontSize: 12,
        color: TEXT,
        lineHeight: 1.45,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {thread.preview || "-"}
    </Typography>
  );
}

function ConversationListItem({ thread, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.6,
        py: 1.25,
        cursor: "pointer",
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: selected ? "#F8FAFF" : "#fff",
        boxShadow: selected ? "inset 0 -2px 0 #D7E6FF" : "none",
        transition: "background-color 150ms ease",
        "&:hover": { backgroundColor: "#F9FAFB" },
      }}
    >
      <Box sx={{ display: "flex", gap: 1.1 }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={thread.avatar_url || undefined}
            sx={{
              width: 34,
              height: 34,
              fontSize: 12,
              bgcolor: "#EDE9FE",
              color: PURPLE,
            }}
          >
            {initials(thread.name)}
          </Avatar>
          <Box
            sx={{
              position: "absolute",
              right: 1,
              bottom: 1,
              width: 9,
              height: 9,
              borderRadius: "50%",
              bgcolor: thread.is_online ? "#22C55E" : "#CBD5E1",
              border: "2px solid #fff",
            }}
          />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: selected ? BLUE : "#344054",
                lineHeight: 1.2,
              }}
            >
              {thread.name || "-"}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: MUTED, flexShrink: 0 }}>
              {thread.timestamp || ""}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 10.5, color: MUTED, lineHeight: 1.2, mt: 0.1 }}>
            {thread.handle || ""}
          </Typography>

          <Box sx={{ mt: 0.7 }}>
            <ThreadPreview thread={thread} />
          </Box>
        </Box>

        {Number(thread.unread_count || 0) > 0 ? (
          <Box
            sx={{
              alignSelf: "center",
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: "#22C55E",
              flexShrink: 0,
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
}

function MessageItem({ item, showAvatar, threadName }) {
  const isOutbound = Boolean(item.is_outbound);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOutbound ? "flex-end" : "flex-start",
        gap: 1,
        mt: 1.4,
      }}
    >
      {!isOutbound ? (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 11,
            bgcolor: "#EDE9FE",
            color: PURPLE,
            visibility: showAvatar ? "visible" : "hidden",
          }}
        >
          {initials(threadName)}
        </Avatar>
      ) : null}

      <Box sx={{ maxWidth: 360, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: isOutbound ? "flex-end" : "flex-start",
            gap: 1,
            mb: 0.4,
          }}
        >
          <Typography sx={{ fontSize: 10.5, color: MUTED }}>
            {isOutbound ? "You" : threadName}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: MUTED }}>
            {item.time_label || ""}
          </Typography>
        </Box>

        {item.message_kind === "attachment" ? (
          <Box
            sx={{
              minWidth: 210,
              border: `1px solid ${BORDER}`,
              borderRadius: 1.75,
              px: 1.2,
              py: 0.95,
              bgcolor: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 17, color: PURPLE }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 11.75,
                    fontWeight: 600,
                    color: TEXT,
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.attachment_name || "Attachment"}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                  {item.attachment_size || ""}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: isOutbound ? "#E7F3FF" : "#fff",
              border: `1px solid ${isOutbound ? "#D3E7FF" : BORDER}`,
              borderRadius: 1.75,
              px: 1.25,
              py: 0.95,
              boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TEXT, lineHeight: 1.6 }}>
              {item.body_text || ""}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function DirectMessagesWorkspace({
  threads = [],
  selectedThread = null,
  searchValue = "",
  onSearchChange,
  onSelectThread,
  messages = [],
  composerValue = "",
  onComposerChange,
  onSend,
  sending = false,
  unreadCount = 0,
  onEmail,
  onCall,
  onDelete,
  onMore,
}) {
  const groupedItems = useMemo(() => {
    let previousDay = null;

    return messages.flatMap((item) => {
      const shouldShowDay = item.day_label && item.day_label !== previousDay;
      previousDay = item.day_label || previousDay;
      return [
        shouldShowDay
          ? {
              type: "day",
              key: `day-${item.day_label}-${item.id}`,
              label: item.day_label,
            }
          : null,
        { type: "message", key: `message-${item.id}`, item },
      ].filter(Boolean);
    });
  }, [messages]);

  const unreadLabel = `${Number(unreadCount || 0)} Unread Messages`;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 0.2,
          pt: 1.5,
          pb: 1.1,
          borderTop: `1px solid ${LIGHT_BLUE}`,
          borderBottom: `1px solid ${LIGHT_BLUE}`,
        }}
      >
        <Typography sx={{ color: BLUE, fontSize: 15, fontWeight: 800 }}>
          Chat
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{unreadLabel}</Typography>
      </Box>

      <Box
        sx={{
          mt: 1.8,
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
          minHeight: 550,
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ borderRight: { xs: "none", md: `1px solid ${BORDER}` } }}>
          <Box sx={{ p: 1.6, borderBottom: `1px solid ${BORDER}` }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search..."
              value={searchValue}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: PURPLE }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 36,
                  borderRadius: 1.5,
                  fontSize: 12.5,
                },
              }}
            />
          </Box>

          <Box sx={{ maxHeight: 494, overflowY: "auto" }}>
            {threads.map((thread) => (
              <ConversationListItem
                key={thread.id}
                thread={thread}
                selected={Number(selectedThread?.id) === Number(thread.id)}
                onClick={() => onSelectThread?.(thread)}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar
                  src={selectedThread?.avatar_url || undefined}
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: 12,
                    bgcolor: "#EDE9FE",
                    color: PURPLE,
                  }}
                >
                  {initials(selectedThread?.name)}
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    right: 1,
                    bottom: 1,
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: selectedThread?.is_online ? "#22C55E" : "#CBD5E1",
                    border: "2px solid #fff",
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT }}>
                  {selectedThread?.name || "-"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }}>
                  {selectedThread?.handle || ""}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={0.25}>
              <IconButton size="small" onClick={onEmail}>
                <MailOutlineRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />
              </IconButton>
              <IconButton size="small" onClick={onCall}>
                <PhoneOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />
              </IconButton>
              <IconButton size="small" onClick={onDelete}>
                <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />
              </IconButton>
              <IconButton size="small" onClick={onMore}>
                <MoreVertRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, px: 2, py: 1.6, overflowY: "auto", minHeight: 0 }}>
            {groupedItems.map((entry, index) => {
              if (entry.type === "day") {
                return (
                  <Box
                    key={entry.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      my: 1.5,
                    }}
                  >
                    <Divider sx={{ flex: 1, borderColor: LIGHT_BLUE }} />
                    <Typography sx={{ fontSize: 10.5, color: MUTED }}>
                      {entry.label}
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: LIGHT_BLUE }} />
                  </Box>
                );
              }

              const previous = groupedItems[index - 1];
              const previousMessage = previous?.type === "message" ? previous.item : null;

              return (
                <MessageItem
                  key={entry.key}
                  item={entry.item}
                  showAvatar={
                    !entry.item.is_outbound &&
                    (!previousMessage ||
                      previousMessage.sender_name !== entry.item.sender_name ||
                      previousMessage.day_label !== entry.item.day_label)
                  }
                  threadName={selectedThread?.name || ""}
                />
              );
            })}
          </Box>

          <Box sx={{ p: 1.6, borderTop: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  border: `1px solid ${PURPLE}`,
                  color: PURPLE,
                }}
              >
                <AddRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>

              <TextField
                size="small"
                fullWidth
                placeholder="Message"
                value={composerValue}
                onChange={onComposerChange}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    onSend?.();
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: 1.5,
                    fontSize: 12.5,
                  },
                }}
              />

              <Button
                className={withPillButtonClassName()}
                onClick={onSend}
                disabled={sending || !String(composerValue || "").trim()}
                startIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                sx={[
                  pillButtonInteractiveSx,
                  pillButtonFilledSx,
                  {
                    height: 36,
                    minWidth: 86,
                    fontSize: 12,
                    fontWeight: 400,
                    boxShadow: "none",
                  },
                ]}
              >
                {sending ? "Sending..." : "Send"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
